/**
 * @ngdoc services
 * @memberof mercury.modules.time
 * @name TimeClock
 * @description Data access service for managaing time clocks
 */
angular.module('mercury.modules.time.clock')
.factory('TimeClock', ["$http", "$q", "$resource", "$window", "settings", "shiftHelpers", function($http, $q, $resource, $window, settings, shiftHelpers) {
  var TimeClocks = $resource(settings.mercHost + '/time-clockins/:id', {id: '@id'});
  var TimeClocksExport = $resource(settings.mercHost + '/time-clockins/export:id', {id: '@id'});
  var RoleGroupBy = 'role';
  var StaffGroupBy = 'staff';

  /**
   * Get all time clocks that overlap the given time period
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of time period
   * @param  {String} endTime   End of time period
   */
  function getAll(startTime, endTime) {
    return TimeClocks.query({
      startTime: moment(startTime).mUtcFormat(),
      endTime: moment(endTime).mUtcFormat(),
      includeInactive: true
    }).$promise;
  }

  /**
   * Get a list of all active time clocks that overlap a given period.
   * This will exclude clocks without a clockout time and clocks with a DatastampDeleted set
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of time period
   * @param  {String} endTime   End of time period
   */
  function getAllActive(startTime, endTime) {
    return TimeClocks.query({
      startTime: moment(startTime).mUtcFormat(),
      endTime: moment(endTime).mUtcFormat()
    }).$promise;
  }

  /**
   * Get a list of all active time clocks that overlap a given period.
   * This will exclude clocks with a DatastampDeleted set
   * All clocks with that haven't clocked out will assume that the current time is their clockout
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of time period
   * @param  {String} endTime   End of time period
   */
  function getAllActiveAndClockedIn(startTime, endTime) {
    return TimeClocks.query({
      startTime: moment(startTime).mUtcFormat(),
      endTime: moment(endTime).mUtcFormat(),
      includeInactive: false,
      includeLiveCalculation: true
    }).$promise;
  }

  /**
   * Get the last unpaid clock for the specified staff member, before the specified time
   *
   * @memberof TimeClock
   * @param  {String} staffMemberId Id of the staff member to get the clock for
   * @param  {String} time          Cut off time for the clock
   */
  function getLastUnpaid(staffMemberId, time) {
    return TimeClocks.get({staffMemberId: staffMemberId, time: time}).$promise;
  }

  /**
   * Get all the clocks for a specifc staff member that overlap a time range
   *
   * @memberof TimeClock
   * @param  {String} staffMemberId Id of the staff member
   * @param  {String} startTime     Start of the time range
   * @param  {String} endTime       End of the time range
   */
  function getStaffClocks(staffMemberId, startTime, endTime) {
    return TimeClocks.query({staffMemberId: staffMemberId, startTime: startTime, endTime: endTime}).$promise;
  }

  /**
   * Get a summary of cost and hours, grouped by clock role.
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of the time range
   * @param  {String} endTime   End of the time range
   */
  function getSummaryByRole(startTime, endTime, companyId) {
    var deferred = $q.defer();
    var config = {params: {startTime: startTime, endTime: endTime, groupBy: RoleGroupBy}};

    if (companyId) {
      config.headers = {
        'x-loaded-company-id': companyId,
        'x-loaded-suppress-subscription-errors': '1'
      };
    }

    $http.get(settings.mercHost + '/time/clockins/summary/group-by', config)
    .then(function(result) {
      deferred.resolve(result.data);
    }, function(result) {
      deferred.reject(result.data);
    });

    return deferred.promise;
  }

  /**
   * Get a summary of cost and hours, grouped by clock staff member
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of the time range
   * @param  {String} endTime   End of the time range
   */
  function getSummaryByStaff(startTime, endTime) {
    var deferred = $q.defer();

    $http.get(settings.mercHost + '/time/clockins/summary/group-by', {
      params: {startTime: startTime, endTime: endTime, groupBy: StaffGroupBy}
    })
    .then(function(result) {
      deferred.resolve(result.data);
    }, function(result) {
      deferred.reject(result.data);
    });

    return deferred.promise;
  }

  /**
   * Get a summary of cost and hours, grouped by interval
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of the time range
   * @param  {String} endTime   End of the time range
   * @param  {String} interval  Interval length
   */
  function getSummaryByInterval(startTime, endTime, interval) {
    var deferred = $q.defer();

    $http.get(settings.mercHost + '/time/clockins/summary', {
      params: {startTime: startTime, endTime: endTime, interval: interval}
    })
    .then(function(result) {
      deferred.resolve(result.data);
    }, function(result) {
      deferred.reject(result.data);
    });

    return deferred.promise;
  }

  /**
   * Helper function to calculate time diff in hours as decimal 2 places
   *
   * @memberof TimeClock
   * @param  {String} endTime   End of range
   * @param  {String} startTime Start of range
   */
  function timeDiffLengthRounded(endTime, startTime) {
    return Math.round(moment(endTime).diff(startTime, 'minutes') / 60 * 100) / 100;
  }

  /**
   * Helper function to calculate time diff in hours without rounding
   *
   * @memberof TimeClock
   * @param  {String} endTime   End of range
   * @param  {String} startTime Start of range
   */
  function timeDiffLengthUnrounded(endTime, startTime) {
    return moment(endTime).diff(startTime, 'minutes') / 60;
  }

  /**
   * Helper function to calculate the length of a clockin in hours without rounding
   *
   * @memberof TimeClock
   * @param  {String} endTime   End of range
   * @param  {String} startTime Start of range
  */
  function getClockinLengthUnrounded(endTime, startTime) {
    return timeDiffLengthUnrounded(endTime, startTime);
  }

  /**
   * Helper function to calculate the length of a clockin in hours as decimal 2 places
   *
   * @memberof TimeClock
   * @param  {String} endTime   End of range
   * @param  {String} startTime Start of range
  */
  function getClockinLength(endTime, startTime) {
    return timeDiffLengthRounded(endTime, startTime);
  }

  /**
   * Helper function to calculate the length of an rule in hours as decimal 2 places
   *
   * @memberof TimeClock
   * @param  {String} endTime   End of range
   * @param  {String} startTime Start of range
   */
  function getRuleLength(endTime, startTime) {
    return timeDiffLengthRounded(endTime, startTime);
  }

  /**
   * Helper function which trims the input clocks to the given time range
   *
   * @memberof TimeClock
   * @param  {Object} clocks    Array of clocks
   * @param  {String} startTime Start of range
   * @param  {String} endTime   End of range
   */
  function effectiveClocks(clocks, startTime, endTime) {
    var results = [];
    angular.forEach(clocks, function(clock) {
      if (moment(clock.clockinTime).isBefore(endTime) && moment(clock.clockoutTime).isAfter(startTime)) {
        var effectiveClock = angular.copy(clock);

        effectiveClock.clockinTime = moment.max(moment(effectiveClock.clockinTime), moment(startTime)).format();
        effectiveClock.clockoutTime = moment.min(moment(effectiveClock.clockoutTime), moment(endTime)).format();

        effectiveClock.length = getClockinLength(effectiveClock.clockoutTime, effectiveClock.clockinTime) -
        (shiftHelpers.calculateTotalUnpaidBreakDuration(clock).toFixed(2));
        effectiveClock.lengthUnrounded = getClockinLengthUnrounded(effectiveClock.clockoutTime, effectiveClock.clockinTime) -
        (shiftHelpers.calculateTotalUnpaidBreakDuration(clock));
        effectiveClock.sortKey = moment(effectiveClock.clockinTime).valueOf();

        // for each rule calculate a length in hours or use the override times if one exists
        angular.forEach(effectiveClock.rules, function(rule) {
          if (rule.processedTotalHours != null) {
            rule.length = rule.processedTotalHours;
          } else {
            if (rule.override) {
              rule.length = getRuleLength(rule.override.appliedTo, rule.override.appliedFrom);
            } else {
              rule.length = getRuleLength(rule.endTime, rule.startTime);
            }
            rule.length = rule.length - shiftHelpers.calculateTotalUnpaidBreakDuration(clock);
          }
        });

        results.push(effectiveClock);
      }
    });
    return results;
  }

  /**
   * Create a new time clock
   *
   * @memberof TimeClock
   * @param  {Object} clock Timeclock model
   */
  function create(clock) {
    return TimeClocks.save(clock).$promise;
  }

  /**
   * Update a time clcok
   *
   * @memberof TimeClock
   * @param  {Object} clock Updated clock model
   */
  function update(clock) {
    // Small hack for getting around the problem where changing the hourlyRate updates
    // the newClock's hourlyRate from undefined to null.
    // the latter will trigger a validation error
    // See LOADED-1226
    if (clock.hourlyRate === null) {
      delete clock.hourlyRate;
    }
    return TimeClocks.update(clock).$promise;
  }

  /**
   * Download the range of clocks in a given payroll format
   *
   * @memberof TimeClock
   * @param  {String} startTime Start of the range
   * @param  {String} endTime   End of the range
   * @param  {String} format    Format to export to.
   */
  function downloadExport(startTime, endTime, format) {
    TimeClocksExport.get({
      startTime: moment(startTime).mUtcFormat(),
      endTime: moment(endTime).mUtcFormat(),
      format: format
    }).$promise.then(function(result) {
      $window.location.assign(result.location);
    });
  }

  return {
    getAll: getAll,
    getAllActive: getAllActive,
    getLastUnpaid: getLastUnpaid,
    getStaffClocks: getStaffClocks,
    getSummaryByRole: getSummaryByRole,
    getSummaryByStaff: getSummaryByStaff,
    getSummaryByInterval: getSummaryByInterval,
    effectiveClocks: effectiveClocks,
    create: create,
    update: update,
    downloadExport: downloadExport,
    getClockinLength: getClockinLength,
    getRuleLength: getRuleLength,
    getClockinLengthUnrounded: getClockinLengthUnrounded,
    getAllActiveAndClockedIn: getAllActiveAndClockedIn
  };
}]);
